import _ from 'lodash';
import { REVERSE_CONDITION_MAP, REVERSE_MACHINE_STATE_MAP, REVERSE_CRITICALITY_MAP } from 'common/constants';
import { filtersIncludedInCharts, getMachinesAnalyticsMetadata } from 'home/MachinesAnalytics/machinesAnalytics.constants';
import { convertTimestringsToDateObjects, formatDateWithLocalizedMonthAbbr, getDefectAbbreviation } from 'common/utils';
import { formatDataForCount, getTranslatedLabel } from 'home/Widget/actions/widget.actions';
import { getDefectDisplayName } from 'common/utils/defectDisplayName';
import colors from 'common/styles/colors';
import { getCurrencySymbol, getCurrencyFromState } from 'common/utils/currencyUtils';
import i18n from '../../i18n/config';

const CONDITION_TO_I18N = {
  Good: 'widgetLibrary.conditions.good',
  Slight: 'widgetLibrary.conditions.slight',
  Moderate: 'widgetLibrary.conditions.moderate',
  Warning: 'widgetLibrary.conditions.warning',
  Critical: 'widgetLibrary.conditions.critical',
  Unknown: 'widgetLibrary.conditions.unknown'
};

export const getShaftName = (shaftNumber, shaftCount) => {
  switch (shaftNumber) {
    case 1:
      return 'Input Shaft';
    case shaftCount:
      return 'Output Shaft';
    default:
      return `Intermediate Shaft ${shaftNumber - 1}`;
  }
};

export const getHSColor = (score, levels) => {
  if (score === null) return null;
  const { health_score: hsMetadata } = getMachinesAnalyticsMetadata(i18n.t.bind(i18n));
  for (let i = 0; i < hsMetadata.filterOptions[levels].length; i++) {
    const r = hsMetadata.filterOptions[levels][i].value;
    if (r !== 'unknown' && (
      r[0] === 0 && score === 0 || r[0] < score && score <= r[1]
    )) {
      return hsMetadata.colors[levels][i];
    }
  }
  return null;
};

export const getSearchParamFilters = (searchParams, forCharts = false) => {
  const filters = [];
  let filterKeyName = '';
  Object.keys(searchParams).forEach((filterKey) => {
    if (
      filterKey === 'searchKey' ||
      (forCharts && !filtersIncludedInCharts.includes(filterKey))
    ) {
      return;
    }
    if (searchParams[filterKey].length > 0) {
      if (filterKey === 'state') {
        filterKeyName = 'current_state';
      } else if (filterKey === 'area') {
        filterKeyName = 'area_name';
      } else if (filterKey === 'transmission') {
        filterKeyName = 'component_subtype';
      } else if (filterKey === 'location') {
        filterKeyName = 'locations';
      } else {
        filterKeyName = filterKey;
      }
      const filter = {
        name: filterKeyName,
        op: 'in',
        value: searchParams[filterKey].map((filterValue) => {
          if (filterKey === 'criticality') {
            return REVERSE_CRITICALITY_MAP[filterValue];
          }
          if (filterKey === 'condition') {
            return REVERSE_CONDITION_MAP[filterValue];
          } if (filterKey === 'state') {
            return REVERSE_MACHINE_STATE_MAP[filterValue];
          } if (
            filterKey === 'location' ||
            filterKey === 'health_score'
          ) {
            return filterValue.value;
          } if (filterKey === 'area') {
            return filterValue.name;
          } return filterValue;
        })
      };
      if (
        filterKey === 'vfd' || filterKey === 'asset_setup' || filterKey === 'events' ||
        filterKey === 'has_sensors' || filterKey === 'has_poor_wifi' ||
        filterKey === 'has_unreviewed_alarms' || filterKey === 'watchlist'
      ) {
        filter.op = 'eq';
        filter.value = searchParams[filterKey][0].value;
      }
      filters.push(filter);
    }
  });
  return filters;
};

export const formatAssetSetupData = (data, metadata) => {
  const filterOptions = [];

  const numberOfBars = Object.keys(data).length;
  let finalData = [...Array(numberOfBars + 1)].map(() => [null]);
  finalData[0][0] = i18n.t('widgetLibrary.typeOfSetup');

  // wait for metadata.sortOrder to be populated post clear filters action
  if (metadata.sortOrder) {
    Object.keys(metadata.sortOrder).forEach((step_grp, sortIndex) => {
      filterOptions.push({ groupName: step_grp });
      finalData[sortIndex + 1][0] = getTranslatedLabel(step_grp);
      const fields = !_.isEmpty(metadata.sortOrder[step_grp]) ? metadata.sortOrder[step_grp] : Object.keys(data[step_grp]);
      fields.forEach((field) => {
        if (_.isNil(data[step_grp][field])) return;
        filterOptions.push({ text: field, value: { field, step_grp } });
        finalData[0].push({
          label: getTranslatedLabel(field),
          type: 'number'
        });
        for (let i = 1; i <= numberOfBars; i++) {
          if (i !== (sortIndex + 1)) finalData[i].push(null);
          else finalData[i].push(data[step_grp][field]);
        }
      });
    });

    finalData = finalData.filter(row => _.some(row.slice(1), Boolean));
  }

  if (finalData.length === 1) {
    finalData = i18n.t('widgetLibrary.setupComplete');
  }

  return [finalData, filterOptions];
};

export const formatEventsData = (data, metadata) => {
  const finalData = Object.keys(data)
    .map(item => [item, data[item]]);
  const filterOptions = Object.keys(data)
    .map(key => ({ text: key, value: key }));
  let colorIdx = 0;
  for (let i = 0; i < finalData.length; i++) {
    if (finalData[i][0].includes('critical') || finalData[i][0] === 'device_unreachable') {
      finalData[i].push(colors.critical);
    } else if (finalData[i][0].includes('warning')) {
      finalData[i].push(colors.warning);
    } else {
      finalData[i].push(metadata.colors[colorIdx]);
      colorIdx = (colorIdx + 1) % metadata.colors.length;
    }
    finalData[i][0] = getTranslatedLabel(finalData[i][0]);
  }
  return [finalData, filterOptions];
};

export const formatPlantTrendData = (data, metadata) => {
  const translatedLabels = metadata.conditionLabels.map(l => (CONDITION_TO_I18N[l] ? i18n.t(CONDITION_TO_I18N[l]) : l));
  const titleRow = [i18n.t('widgetLibrary.assetCondition'), ...translatedLabels];
  const keys = metadata.conditionLabels.map(l => l.toLowerCase());
  let trendData = data.data.map((obj) => {
    const healthObj = Object.assign({}, obj);
    healthObj.time = new Date(obj.time * 1000);
    healthObj.day = formatDateWithLocalizedMonthAbbr(healthObj.time);
    return healthObj;
  });
  trendData = trendData.slice(trendData.length - 7, trendData.length);
  const formattedData = trendData.map(d => [
    d.day, ...keys.map(key => d[key])
  ]);
  return [[titleRow, ...formattedData], null];
};

export const formatEventTrendData = (res) => {
  const data = [[i18n.t('widgetLibrary.eventsCount'), i18n.t('widgetLibrary.chartLabels.open'), i18n.t('widgetLibrary.chartLabels.closed')]];
  const yDataOpen = res.open_count;
  const yDataClosed = res.closed_count;
  const xData = convertTimestringsToDateObjects(res.timestamps);
  if (!yDataOpen || !yDataClosed || !xData) return data;
  for (let i = 0; i < xData.length; i++) {
    data[i + 1] = [formatDateWithLocalizedMonthAbbr(xData[i]), yDataOpen[i], yDataClosed[i]];
  }
  return [data, null];
};

export const formatCostSavedByMachineData = (res, metadata, state = null) => {
  // Get currency from state if provided, otherwise default to USD
  const currency = state ? getCurrencyFromState(state) : 'USD';
  const currencySymbol = getCurrencySymbol(currency);

  const machineColumn = i18n.t('widgetLibrary.chartLabels.machine');
  const costSavedColumn = i18n.t('widgetLibrary.chartLabels.costSavedWithCurrency', { currencySymbol });
  const noDataLabel = i18n.t('widgetLibrary.chartLabels.noData');

  if (!res || !res.machines || res.machines.length === 0) {
    return [[[machineColumn, costSavedColumn, { role: 'style' }], [noDataLabel, 0, colors.greyL]], null];
  }

  const data = [[machineColumn, costSavedColumn, { role: 'style' }]];
  // Backend already sorts machines by cost_saved in descending order.
  // Limit to top 10 machines to keep the chart readable when there are many.
  res.machines.slice(0, 10).forEach((machine) => {
    data.push([
      machine.machine_name || i18n.t('widgetLibrary.chartLabels.machineWithId', { id: machine.machine_id }),
      machine.cost_saved || 0,
      colors.green
    ]);
  });

  return [data, null];
};

export const formatDefectsTrendData = (res, metadata) => {
  const barData = [[
    i18n.t('widgetLibrary.defectNames', { defaultValue: 'Defect names' }),
    i18n.t('widgetLibrary.numberOfDefects'),
    { role: 'style' },
    { type: 'string', role: 'tooltip' },
  ]];
  let colorIdx = 0;
  res.bar.forEach((defect, i) => {
    const label = getDefectAbbreviation(defect.name);
    const displayName = getDefectDisplayName(i18n.t.bind(i18n), {
      defectId: defect.id,
      defectName: defect.name,
    });
    const tooltip = `${displayName} (${defect.count})`;
    barData[i + 1] = [
      label, defect.count, metadata.colors[colorIdx], tooltip
    ];
    colorIdx += 1;
  });
  barData.sort((a, b) => {
    if (a[3] === 'Others' || a[1] < b[1]) return 1;
    if (b[3] === 'Others' || a[1] > b[1]) return -1;
    return 0;
  });
  const rawData = res.bar;
  res.bar = barData;
  return [res, { account_id: metadata.account_id, rawData }];
};

export const formatUnreadEventsData = (res, metadata) => {
  const [data] = formatDataForCount(res, metadata);
  return [data, { account_id: metadata.account_id }];
};

/**
 * Extracts HP (horsepower) value from a component or asset object.
 */
export const extractHorsepower = (obj) => {
  if (!obj) return null;
  if (obj.power_hp !== undefined && obj.power_hp !== null) return obj.power_hp;
  if (obj.hp !== undefined && obj.hp !== null) return obj.hp;
  if (obj.power !== undefined && obj.power !== null) return obj.power;
  return null;
};

/**
 * Formats speed value to Hz string.
 */
const formatSpeedHz = (value, alreadyHz = false) => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  const hz = alreadyHz ? numeric : numeric / 60;
  return `${hz.toFixed(2)} Hz`;
};

/**
 * Extracts and formats speed value from a component or asset object.
 */
export const extractSpeed = (obj) => {
  if (!obj) return null;
  if (obj.speed_hz !== undefined && obj.speed_hz !== null) {
    return formatSpeedHz(obj.speed_hz, true);
  }
  if (obj.speed_rpm !== undefined && obj.speed_rpm !== null) {
    return formatSpeedHz(obj.speed_rpm);
  }
  if (obj.speed !== undefined && obj.speed !== null) {
    return formatSpeedHz(obj.speed);
  }
  return null;
};

/**
 * Extracts HP and RPM information from asset components.
 * Returns arrays of component info objects with heading and value properties.
 */
export const extractComponentHpAndRpm = (asset) => {
  if (!asset || !asset.components) {
    return { hpInfo: [], rpmInfo: [] };
  }

  const comps = Object.values(asset.components);
  comps.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const hpList = [];
  const rpmList = [];

  comps.forEach((comp) => {
    const hp = extractHorsepower(comp);
    if (hp !== null) {
      hpList.push({ heading: comp.name || 'Unknown', value: `${hp} HP` });
    }

    const speedHz = extractSpeed(comp);
    if (speedHz) {
      rpmList.push({ heading: comp.name || 'Unknown', value: speedHz });
    }
  });

  return { hpInfo: hpList, rpmInfo: rpmList };
};
